import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DxNumberBoxModule, DxSelectBoxModule, DxTextAreaModule, DxTextBoxModule } from 'devextreme-angular';
import { GenericPopupComponent, PopupButton } from '../generic-popup/generic-popup.component';
import { GpdButtonComponent } from '../gpd-button/gpd-button.component';
import { GpdCheckboxComponent } from '../gpd-checkbox/gpd-checkbox.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { DeletePopupComponent } from '../delete-popup/delete-popup.component';
import { ContactsDropdownBoxComponent } from '../contacts-dropdown-box/contacts-dropdown-box.component';
import { ValidationToastComponent } from '../validation-toast/validation-toast.component';
import { ContactService } from 'src/app/services/contact.service';
import { AppGlobals } from 'src/app/app.globals';
import {
  TaskRule,
  TaskRuleAction,
  TaskRuleActionType,
  TaskRuleCondition,
  TaskRuleConditionGroup,
  TaskRuleField,
  TaskRuleOperator,
} from 'src/app/interfaces/entities/task-rule';
import { TaskTypeEnum } from 'src/app/pages/config/project-config/project-structure-config/project-task-config/datatypes-taskconfig';
import {
  ACTION_LABELS,
  ERR,
  MICROTASK_TYPES,
  NO,
  Option,
  PRIORITIES,
  RECIPIENTS,
  RuleEditorContext,
  RuleTaskInfo,
  STATUSES,
  UNITS,
  YES,
  actionError,
  actionsFor,
  cloneRule,
  conditionError,
  conditionOperators,
  conflictsFor,
  fieldLabel,
  fieldsFor,
  hasResult,
  newAction,
  newCondition,
  newRule,
  normalizeForSave,
  operatorLabel,
  previewText,
  repetitionLocked,
  ruleError,
  ruleHasErrors,
  settableStatuses,
  summaryText,
  timingErrors,
  valueKindOf,
} from './task-rule-catalog';

/** Rule as edited in the popup: a stable key for the list, plus whether it was ever applied. */
interface EditableRule extends TaskRule {
  _key: number;
  _saved: boolean;
}

export interface TaskRulesPopupTask {
  id?: number;
  title?: string;
  nr?: string;
  type: string;
  validation?: boolean;
  userDefinedFields?: string | string[];
  rules?: TaskRule[];
}

export interface TaskRulesPopupOtherTask extends TaskRulesPopupTask {
  archived?: string | null;
}

/**
 * "Rules for <task>" (#4962-#4965): rule list on the left, editor of the selected rule on the
 * right. Works on a copy: "Apply" hands the rules to the task form, which saves them with the
 * task template; "Cancel" discards everything changed here.
 */
const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

@Component({
  selector: 'app-task-rules-popup',
  imports: [
    CommonModule,
    FormsModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxNumberBoxModule,
    DxTextAreaModule,
    GenericPopupComponent,
    GpdButtonComponent,
    GpdCheckboxComponent,
    SvgIconComponent,
    DeletePopupComponent,
    ContactsDropdownBoxComponent,
    ValidationToastComponent,
  ],
  templateUrl: './task-rules-popup.component.html',
  styleUrl: './task-rules-popup.component.css',
  // Written for Eager CD. Under Angular 22's implicit OnPush the rules open() loads stayed hidden on the first show.
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TaskRulesPopupComponent implements OnInit, OnChanges {
  visible = model(false);

  @Input({ required: true }) task!: TaskRulesPopupTask;
  /** Every task template (for "Other task" and "Copy existing rule"). */
  @Input() allTasks: TaskRulesPopupOtherTask[] = [];
  @Input() departments: { id: number; name: string }[] = [];
  @Input() users: { emailAddress: string; displayName: string }[] = [];
  @Input() taskPackages: { id: number; name: string }[] = [];
  @Input() contractTypes: { id: number; name: string }[] = [];
  @Input() contractStatuses: { id: number; status: string }[] = [];

  @Output() apply = new EventEmitter<TaskRule[]>();

  rules: EditableRule[] = [];
  selectedKey: number | null = null;
  showErrors = false;
  addMenuOpen = false;
  copyMode = false;
  copySearch = '';
  deleteConfirmVisible = false;
  ctx!: RuleEditorContext;

  persons: any[] = [];
  companies: any[] = [];

  private nextKey = 1;

  readonly STATUSES = STATUSES;
  readonly UNITS = UNITS;
  readonly PRIORITIES = PRIORITIES;
  readonly RECIPIENTS = RECIPIENTS;
  readonly MICROTASK_TYPES = MICROTASK_TYPES;
  readonly ERR = ERR;
  readonly TaskTypeEnum = TaskTypeEnum;
  readonly triggers: Option[] = [
    { value: 'Immediately', text: $localize`:@@ruleTriggerNow:immediately` },
    { value: 'Delayed', text: $localize`:@@ruleTriggerDelayed:delayed` },
  ];
  readonly repeats: Option[] = [
    { value: 'Once', text: $localize`:@@ruleRepeatOnce:once` },
    { value: 'Repeat', text: $localize`:@@ruleRepeatRepeat:repeat` },
  ];
  readonly intervals: Option[] = [
    { value: 'Daily', text: $localize`:@@ruleIntervalDaily:daily` },
    { value: 'Weekly', text: $localize`:@@ruleIntervalWeekly:weekly` },
    { value: 'Monthly', text: $localize`:@@ruleIntervalMonthly:monthly` },
    { value: 'Custom', text: $localize`:@@ruleIntervalCustom:custom` },
  ];
  readonly properties: Option[] = [
    { value: 'Status', text: $localize`:@@ruleFieldStatus:Status` },
    { value: 'Result', text: $localize`:@@ruleResult:Result` },
  ];
  readonly yesNoOptions: Option[] = [
    { value: YES, text: $localize`:@@ruleYes:Yes` },
    { value: NO, text: $localize`:@@ruleNo:No` },
  ];
  readonly responsibleKinds: Option[] = [
    { value: 'User', text: $localize`:@@ruleRecipientUser:Fixed user` },
    { value: 'Department', text: $localize`:@@ruleResponsibleDepartment:Department` },
  ];

  readonly notAvailable = $localize`:@@ruleNotAvailable:(not available)`;
  readonly noName = $localize`:@@ruleNoName:Without name`;
  /** The fade under the sticky preview only shows once the editor is scrolled. */
  editorScrolled = false;

  readonly previewTitle = $localize`:@@rulePreview:Plain-text preview`;

  buttons: PopupButton[] = [
    { text: $localize`:@@cancel:Cancel`, type: 'secondary', onClick: () => this.cancel(), closeOnClick: false },
    { text: $localize`:@@ruleApply:Apply rules`, type: 'primary', icon: 'tick', onClick: () => this.applyRules(), closeOnClick: false },
  ];
  leftButtons: PopupButton[] = [
    { text: $localize`:@@ruleRemove:Remove rule`, type: 'danger-outline', icon: 'delete', onClick: () => this.askDelete(), closeOnClick: false },
  ];

  constructor(private contactService: ContactService) {}

  ngOnInit(): void {
    Promise.all([this.contactService.getPersons({}), this.contactService.getCompanies({})])
      .then(([persons, companies]) => {
        this.persons = persons?.personItems ?? [];
        this.companies = companies?.companyItems ?? [];
      })
      .catch(() => {
        // the contact pickers stay empty; everything else works
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['task'] || changes['allTasks'] || changes['departments'] || changes['users'] || changes['contractTypes'])
      this.buildContext();
  }

  get title(): string {
    return $localize`:@@ruleTitle:Rules for ${this.task?.title ?? ''}:task:`;
  }

  get selected(): EditableRule | undefined {
    return this.rules.find(r => r._key === this.selectedKey);
  }

  /** Called by the popup's onShowing: start from the rules the form currently holds. */
  open(): void {
    this.buildContext();
    this.rules = (this.task.rules ?? []).map(r => this.editable(cloneRule(r), true));
    this.selectedKey = this.rules[0]?._key ?? null;
    this.showErrors = false;
    this.addMenuOpen = false;
    this.copyMode = false;
  }

  private editable(rule: TaskRule, saved: boolean): EditableRule {
    return { ...rule, _key: this.nextKey++, _saved: saved };
  }

  private buildContext(): void {
    if (!this.task) return;
    const info = (t: TaskRulesPopupTask): RuleTaskInfo => ({
      id: t.id,
      type: t.type,
      title: t.title,
      nr: t.nr,
      validation: !!t.validation,
      options: Array.isArray(t.userDefinedFields)
        ? t.userDefinedFields
        : (t.userDefinedFields ?? '')
            .split(';')
            .map(x => x.trim())
            .filter(x => x),
    });
    this.ctx = {
      task: info(this.task),
      otherTasks: this.allTasks.filter(t => t.id != null && !t.archived).map(info),
      departments: this.departments,
      users: this.users,
      taskPackages: this.taskPackages,
      contractTypes: this.contractTypes,
      contractStatuses: this.contractStatuses,
      contactName: (id, type) => {
        if (!id) return null;
        const list = type === 'company' ? this.companies : this.persons;
        const contact = list?.find((c: any) => String(c.id) === String(id));
        return contact?.displayName ?? contact?.name ?? null;
      },
    };
  }

  /* ============ list ============ */

  select(rule: EditableRule): void {
    this.selectedKey = rule._key;
    this.addMenuOpen = false;
  }

  toggleActive(rule: EditableRule, event?: Event): void {
    event?.stopPropagation();
    rule.isActive = !rule.isActive;
  }

  toggleAddMenu(): void {
    this.addMenuOpen = !this.addMenuOpen;
    this.copyMode = false;
    this.copySearch = '';
  }

  addEmptyRule(): void {
    const rule = this.editable(newRule(), false);
    this.rules.push(rule);
    this.selectedKey = rule._key;
    this.addMenuOpen = false;
  }

  /**
   * All rules of the other task templates, matching the search (#4962 "Copy existing rule").
   * A rule copied to several tasks (same name and definition) is listed once.
   */
  get copyCandidates(): { rule: TaskRule; tasks: TaskRulesPopupOtherTask[] }[] {
    const query = this.copySearch.trim().toLowerCase();
    const byContent = new Map<string, { rule: TaskRule; tasks: TaskRulesPopupOtherTask[] }>();
    for (const task of this.allTasks.filter(t => t.id !== this.task.id)) {
      for (const rule of task.rules ?? []) {
        if (query && !rule.name.toLowerCase().includes(query)) continue;
        const key = this.ruleContentKey(rule);
        const entry = byContent.get(key);
        if (entry) entry.tasks.push(task);
        else byContent.set(key, { rule, tasks: [task] });
      }
    }
    return [...byContent.values()];
  }

  /** Tooltip listing every task that has this rule. */
  copyTaskTitles(tasks: TaskRulesPopupOtherTask[]): string {
    return tasks.map(t => `${t.nr ?? ''} ${t.title ?? ''}`.trim()).join('\n');
  }

  /** Same name and definition = the same rule, set up on several tasks. */
  private ruleContentKey(rule: TaskRule): string {
    return `${rule.name.trim().toLowerCase()}|${JSON.stringify(rule.definition)}`;
  }

  /** Whether the selected rule is also set up on another task; if not, removing it deletes it for good. */
  private get selectedOnOtherTasks(): boolean {
    if (!this.selected) return false;
    const key = this.ruleContentKey(this.selected);
    return this.allTasks.some(t => t.id !== this.task.id && (t.rules ?? []).some(r => this.ruleContentKey(r) === key));
  }

  copyTaskText(tasks: TaskRulesPopupOtherTask[]): string {
    const first = `${tasks[0].nr ?? ''} ${tasks[0].title ?? ''}`.trim();
    return tasks.length === 1 ? first : $localize`:@@ruleCopyMoreTasks:${first}:task: + ${tasks.length - 1}:count: more`;
  }

  copyRule(source: TaskRule): void {
    const copy = this.editable({ ...cloneRule(source), id: null, copiedFromRuleId: source.id ?? null, migrationHint: null }, false);
    this.rules.push(copy);
    this.selectedKey = copy._key;
    this.addMenuOpen = false;
    this.copyMode = false;
    // values that do not exist for this task show up right away as "(not available)"
    this.showErrors = true;
  }

  isInvalid(rule: EditableRule): boolean {
    return this.showErrors && ruleHasErrors(rule, this.ctx);
  }

  summary(rule: EditableRule): string {
    return summaryText(rule, this.ctx);
  }

  /* ============ footer ============ */

  askDelete(): void {
    if (this.selected) this.deleteConfirmVisible = true;
  }

  get deleteTitle(): string {
    return this.selectedOnOtherTasks ? $localize`:@@ruleRemove:Remove rule` : $localize`:@@ruleDelete:Delete rule`;
  }

  get deleteConfirmText(): string {
    return this.selectedOnOtherTasks ? $localize`:@@yesRemove:Yes, remove` : $localize`:@@yesDelete:Yes, delete`;
  }

  /** HTML for the confirm popup (it renders `content` as HTML); names are escaped. */
  get deleteMessage(): string {
    const name = escapeHtml(this.selected?.name || this.noName);
    const task = escapeHtml(this.task?.title ?? '');
    if (this.selectedOnOtherTasks) {
      return `<p>${$localize`:@@ruleRemoveConfirm:Do you want to remove the rule “${name}:name:” from the task “${task}:task:”?`}</p>`;
    }
    return (
      `<p>${$localize`:@@ruleDeleteConfirm:The rule “${name}:name:” is only set up on the task “${task}:task:”. If you remove it, it is deleted for good.`}</p>` +
      `<p>${$localize`:@@ruleDeleteQuestion:Do you want to delete it?`}</p>`
    );
  }

  confirmDelete(): void {
    const index = this.rules.findIndex(r => r._key === this.selectedKey);
    if (index >= 0) this.rules.splice(index, 1);
    this.selectedKey = this.rules[Math.min(index, this.rules.length - 1)]?._key ?? null;
    this.deleteConfirmVisible = false;
  }

  cancel(): void {
    this.visible.set(false);
  }

  applyRules(): void {
    this.showErrors = true;
    const invalid = this.rules.find(r => ruleHasErrors(r, this.ctx));
    if (invalid) {
      this.selectedKey = invalid._key;
      const reason = ruleError(invalid, this.ctx);
      AppGlobals.ShowErrorMessage(
        $localize`:@@ruleIncomplete:“${invalid.name || this.noName}:name:” has missing or invalid values: ${reason}:reason:`
      );
      return;
    }
    this.apply.emit(
      this.rules.map((r, i) => {
        const { _key, _saved, ...rule } = r;
        return { ...normalizeForSave(rule), sortingIndex: i };
      })
    );
    this.visible.set(false);
  }

  /* ============ editor: general ============ */

  get preview(): string {
    return this.selected ? previewText(this.selected, this.ctx) : '';
  }

  /* ============ editor: conditions ============ */

  get multiGroup(): boolean {
    return (this.selected?.definition.groups.length ?? 0) > 1;
  }

  addCondition(group: TaskRuleConditionGroup): void {
    group.conditions.push(newCondition());
  }

  addGroup(): void {
    const groups = this.selected!.definition.groups;
    if (groups.length === 0) groups.push({ conditions: [newCondition()] });
    groups.push({ conditions: [newCondition()] });
  }

  removeGroup(index: number): void {
    this.selected!.definition.groups.splice(index, 1);
  }

  removeCondition(group: TaskRuleConditionGroup, index: number): void {
    group.conditions.splice(index, 1);
    if (group.conditions.length === 0 && this.selected!.definition.groups.length > 1)
      this.selected!.definition.groups.splice(this.selected!.definition.groups.indexOf(group), 1);
  }

  /** Only the fields of this task type; a no longer valid field stays visible as "(not available)". */
  fieldOptions(c: TaskRuleCondition): Option<TaskRuleField>[] {
    const available = Object.keys(fieldsFor(this.ctx.task.type)) as TaskRuleField[];
    const options = available.map(f => ({ value: f, text: fieldLabel(f) }));
    if (c.field && !available.includes(c.field)) options.push({ value: c.field, text: `${fieldLabel(c.field)} ${this.notAvailable}` });
    return this.stable(c, 'field', options);
  }

  onFieldChanged(c: TaskRuleCondition, field: TaskRuleField): void {
    Object.assign(c, newCondition(), { field });
    const operators = Object.keys(conditionOperators(c, this.ctx)) as TaskRuleOperator[];
    if (operators.length === 1) c.operator = operators[0];
  }

  otherTaskOptions(c: TaskRuleCondition): Option<number>[] {
    const options = this.ctx.otherTasks
      .filter(t => t.id !== this.ctx.task.id)
      .map(t => ({ value: t.id!, text: `${t.nr ? t.nr + ' ' : ''}${t.title ?? ''}` }));
    if (c.otherTaskConfigId != null && !options.some(o => o.value === c.otherTaskConfigId)) {
      const self = c.otherTaskConfigId === this.ctx.task.id;
      options.push({ value: c.otherTaskConfigId, text: self ? `${this.task.title}` : `ID ${c.otherTaskConfigId} ${this.notAvailable}` });
    }
    return this.stable(c, 'otherTask', options);
  }

  onOtherTaskChanged(c: TaskRuleCondition, id: number): void {
    c.otherTaskConfigId = id;
    c.property = 'Status';
    c.operator = null;
    c.value = null;
  }

  propertyOptions(c: TaskRuleCondition): Option[] {
    const other = this.ctx.otherTasks.find(t => t.id === c.otherTaskConfigId);
    return this.stable(c, 'property', hasResult(other?.type) ? this.properties : this.properties.slice(0, 1));
  }

  onPropertyChanged(c: TaskRuleCondition, property: 'Status' | 'Result'): void {
    c.property = property;
    const operators = Object.keys(conditionOperators(c, this.ctx)) as TaskRuleOperator[];
    c.operator = operators.length === 1 ? operators[0] : null;
    c.value = null;
    c.value2 = null;
  }

  operatorOptions(c: TaskRuleCondition): Option<TaskRuleOperator>[] {
    const operators = Object.keys(conditionOperators(c, this.ctx)) as TaskRuleOperator[];
    const options = operators.map(op => ({ value: op, text: operatorLabel(op) }));
    if (c.operator && !operators.includes(c.operator)) options.push({ value: c.operator, text: `${operatorLabel(c.operator)} ${this.notAvailable}` });
    return this.stable(c, 'operator', options);
  }

  /** Contract fields have exactly one operator ("is"), shown as part of the field. */
  showOperator(c: TaskRuleCondition): boolean {
    if (!c.field) return false;
    if (c.field === 'OtherTask') return c.otherTaskConfigId != null && this.operatorOptions(c).length > 0;
    return !['ContractType', 'ContractStatus', 'ContractPartyInternal', 'ContractPartyExternal'].includes(c.field);
  }

  onOperatorChanged(c: TaskRuleCondition, op: TaskRuleOperator): void {
    c.operator = op;
    c.value = null;
    c.value2 = null;
    c.contactType = null;
  }

  valueKind(c: TaskRuleCondition) {
    return valueKindOf(c, this.ctx);
  }

  /** Options of a "Selection" value: the own task's options or those of the referenced task. */
  selectionOptions(c: TaskRuleCondition): Option[] {
    const source = c.field === 'OtherTask' ? this.ctx.otherTasks.find(t => t.id === c.otherTaskConfigId) : this.ctx.task;
    return this.stable(c, 'selection', this.withCurrent((source?.options ?? []).map(o => ({ value: o, text: o })), c.value));
  }

  contractTypeOptions(c: TaskRuleCondition): Option[] {
    return this.stable(c, 'contractType', this.withCurrent(this.contractTypes.map(t => ({ value: String(t.id), text: t.name })), c.value));
  }

  contractStatusOptions(c: TaskRuleCondition): Option[] {
    return this.stable(c, 'contractStatus', this.withCurrent(this.contractStatuses.map(t => ({ value: String(t.id), text: t.status })), c.value));
  }

  onContactSelected(c: TaskRuleCondition, e: any): void {
    c.value = e?.data?.id != null ? String(e.data.id) : null;
    c.contactType = e?.type ?? null;
  }

  conditionError(c: TaskRuleCondition): string | null {
    return this.showErrors ? conditionError(c, this.ctx) : null;
  }

  /** The "points to itself" error is shown at once, not only after Apply (#4963). */
  selfReference(c: TaskRuleCondition): boolean {
    return c.field === 'OtherTask' && this.ctx.task.id != null && c.otherTaskConfigId === this.ctx.task.id;
  }

  /* ============ editor: actions ============ */

  actionOptions(a: TaskRuleAction): Option<TaskRuleActionType>[] {
    const available = actionsFor(this.ctx.task.type);
    const options = available.map(t => ({ value: t, text: ACTION_LABELS[t] }));
    if (a.type && !available.includes(a.type)) options.push({ value: a.type, text: `${ACTION_LABELS[a.type]} ${this.notAvailable}` });
    return this.stable(a, 'action', options);
  }

  onActionTypeChanged(a: TaskRuleAction, type: TaskRuleActionType): void {
    Object.keys(a).forEach(k => delete (a as any)[k]);
    Object.assign(a, newAction(type));
  }

  addAction(): void {
    this.selected!.definition.actions.push(newAction());
  }

  removeAction(index: number): void {
    this.selected!.definition.actions.splice(index, 1);
  }

  statusOptions(a: TaskRuleAction): Option[] {
    return this.stable(a, 'status', this.withCurrent(settableStatuses(this.ctx.task.type), a.status));
  }

  departmentOptions(): Option<number>[] {
    return this.stable(this, 'department', this.departments.map(d => ({ value: d.id, text: d.name })));
  }

  /** UI-only choice "fixed user / department" while neither is picked yet. */
  private chosenResponsibleKind = new WeakMap<TaskRuleAction, 'User' | 'Department'>();

  responsibleKind(a: TaskRuleAction): 'User' | 'Department' {
    if (a.departmentId != null && !a.userEmail) return 'Department';
    if (a.userEmail) return 'User';
    return this.chosenResponsibleKind.get(a) ?? 'User';
  }

  onResponsibleKindChanged(a: TaskRuleAction, kind: 'User' | 'Department'): void {
    a.userEmail = null;
    a.departmentId = null;
    this.chosenResponsibleKind.set(a, kind);
  }

  actionError(a: TaskRuleAction): string | null {
    return this.showErrors ? actionError(a, this.ctx) : null;
  }

  conflicts(a: TaskRuleAction): TaskRule[] {
    return this.selected ? conflictsFor(this.selected, a, this.rules) : [];
  }

  conflictText(a: TaskRuleAction, other: TaskRule): string {
    const name = other.name || this.noName;
    return $localize`:@@ruleConflict:Rule “${name}:name:” sets the same value differently. If both apply at the same time, nothing is changed and the conflict is logged.`;
  }

  get showReviewToast(): boolean {
    return !!this.ctx.task.validation && !!this.selected?.definition.actions.some(a => a.type === 'SetResult');
  }

  /* ============ editor: timing ============ */

  get locked(): boolean {
    return this.selected ? repetitionLocked(this.selected) : false;
  }

  get lockedHint(): string {
    return this.selected && !this.selected.definition.actions.some(a => a.type === 'Notify')
      ? $localize`:@@ruleRepeatOnlyNotify:Only notifications can be repeated`
      : $localize`:@@ruleRepeatOneTime:This rule is carried out once`;
  }

  timingErrors() {
    return this.showErrors && this.selected ? timingErrors(this.selected) : {};
  }

  onIntervalChanged(value: string): void {
    const t = this.selected!.definition.timing;
    t.interval = value as any;
    if (value === 'Custom' && (t.everyDays ?? 0) < 2) t.everyDays = 2;
  }

  onRepeatChanged(value: string): void {
    const t = this.selected!.definition.timing;
    t.repeat = value as any;
    if (value === 'Repeat') {
      t.interval ??= 'Daily';
      t.maxNotifications ??= 3;
    }
  }

  /* ============ helpers ============ */

  /**
   * The option lists above are bound to `[items]`; a new array on every change detection makes
   * devextreme-angular re-set the option each pass (data source rebuilt, search input reset).
   * Hand back the previous array while its content is unchanged.
   */
  private optionCache = new WeakMap<object, Map<string, Option<any>[]>>();

  private stable<T extends Option<any>>(owner: object, list: string, options: T[]): T[] {
    let lists = this.optionCache.get(owner);
    if (!lists) this.optionCache.set(owner, (lists = new Map()));
    const previous = lists.get(list) as T[] | undefined;
    if (
      previous &&
      previous.length === options.length &&
      previous.every((o, i) => o.value === options[i].value && o.text === options[i].text)
    )
      return previous;
    lists.set(list, options);
    return options;
  }

  private withCurrent(options: Option[], value: string | null | undefined): Option[] {
    if (value == null || value === '' || options.some(o => o.value === value)) return options;
    return [...options, { value, text: `${value} ${this.notAvailable}` }];
  }

  trackByIndex = (index: number) => index;
}
